import type { OnboardingItem, PanelKey, TenantTemplateSummary } from "../lib/contracts";
import { panelTitle } from "../lib/contracts";
import { ChecklistItemCard } from "./ChecklistItemCard";
import type { EffectiveRole } from "../../../lib/roles";

export type PanelRole = "owner_admin" | "manager_trainer" | "read_only";

export interface PanelProps {
  panelKey: PanelKey;
  items: OnboardingItem[];
  role: PanelRole;
  /** The caller's real effective role — passed through to each item so
   *  per-destination link permissions can be evaluated at the item level.
   *  Falls back to a mapping from ``role`` when omitted (older tests). */
  effectiveRole?: EffectiveRole;
  onAcknowledgeTenantProfile?: () => void;
  ackPending?: boolean;
  templateSummary?: TenantTemplateSummary | null;
  templateSummaryError?: string | null;
}

/** Shared panel shell — hoists the heading + description + item grid. */
export function OnboardingPanel({
  panelKey, items, role, effectiveRole,
  onAcknowledgeTenantProfile, ackPending,
  templateSummary, templateSummaryError,
}: PanelProps) {
  const canOwnerAct = role === "owner_admin";
  const headingId = `panel-heading-${panelKey}`;
  const description = PANEL_DESCRIPTIONS[panelKey];

  // Prefer the explicit effective role; otherwise map the panel role.
  const rowRole: EffectiveRole =
    effectiveRole ??
    (role === "owner_admin"
      ? "owner_admin"
      : role === "manager_trainer"
      ? "manager_trainer"
      : "viewer");

  return (
    <section
      aria-labelledby={headingId}
      data-testid={`onboarding-panel-${panelKey}`}
      className="rounded-lg border bg-white p-5 dark:bg-slate-900"
    >
      <h2 id={headingId} className="text-lg font-semibold">
        {panelTitle(panelKey)}
      </h2>
      {description && (
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      )}

      {panelKey === "template" && (
        <TemplateReadOnlySummary
          summary={templateSummary}
          error={templateSummaryError}
          showFetchStatus={role !== "read_only"}
        />
      )}

      <div className="mt-4 space-y-3">
        {items.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="onboarding-panel-empty">
            No setup items are grouped under this section.
          </p>
        )}
        {items.map((item) => {
          const canAckThisItem =
            canOwnerAct &&
            item.key === "tenant_profile_confirmed" &&
            onAcknowledgeTenantProfile;
          return (
            <ChecklistItemCard
              key={item.key}
              item={item}
              role={rowRole}
              actionSlot={
                canAckThisItem ? (
                  <button
                    type="button"
                    disabled={
                      !!ackPending ||
                      item.prerequisite_ready === false ||
                      item.status === "complete"
                    }
                    onClick={onAcknowledgeTenantProfile}
                    data-testid="tenant-profile-acknowledge-button"
                    className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
                  >
                    {item.status === "complete"
                      ? "Confirmed"
                      : ackPending
                      ? "Confirming…"
                      : "Confirm tenant profile"}
                  </button>
                ) : undefined
              }
            />
          );
        })}
      </div>
    </section>
  );
}

const PANEL_DESCRIPTIONS: Record<PanelKey, string> = {
  "tenant-profile":
    "Confirm the tenant's core identity — name, time zone, and status.",
  organization:
    "Ensure at least one active company and one active branch are configured.",
  "users-roles":
    "Ensure Owner/Admin, Manager/Trainer, and Representative users exist.",
  template:
    "Select and apply a CRM template. Template application happens in the next setup step.",
  pipeline:
    "Verify the tenant's default pipeline holds all eight canonical stages.",
  "contact-import":
    "Confirm the tenant has an authorized user and valid scope for Contact imports.",
  "launch-readiness":
    "Publish at least one approved first-login or quick-start guide so users can self-serve from day one.",
};

interface TemplateSummaryProps {
  summary: TenantTemplateSummary | null | undefined;
  error: string | null | undefined;
  showFetchStatus: boolean;
}

function TemplateReadOnlySummary({
  summary, error, showFetchStatus,
}: TemplateSummaryProps) {
  if (!showFetchStatus) return null;
  if (error) {
    return (
      <p
        className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
        data-testid="template-summary-error"
      >
        {error}
      </p>
    );
  }
  if (!summary) {
    return (
      <p
        className="mt-3 text-sm text-slate-500"
        data-testid="template-summary-empty"
      >
        No template has been selected yet.
      </p>
    );
  }
  return (
    <div
      className="mt-3 rounded-md border p-3 dark:border-slate-700"
      data-testid="template-summary"
    >
      <p className="text-sm">
        <span className="font-medium">{summary.display_name}</span>{" "}
        <span className="text-slate-500">v{summary.template_version}</span>
      </p>
      {summary.target_business_type && (
        <p className="mt-0.5 text-xs text-slate-500">
          Business type: {summary.target_business_type}
        </p>
      )}
      {summary.description && (
        <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
          {summary.description}
        </p>
      )}
      {summary.stages.length > 0 && (
        <ol className="mt-3 space-y-1 text-sm">
          {summary.stages.map((s) => (
            <li key={s.canonical_stage}>
              <span className="font-mono text-xs text-slate-500">{s.order}.</span>{" "}
              {s.label}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
